import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { menuTranslations } from '../utils/initializeData';

// Apply lang/dir to <html> IMMEDIATELY. This is called from this module (which
// runs before ReactDOM.render in index.tsx), so `dir="rtl"` is already correct
// on the very first paint — not only after LanguageContext's effects run.
export const applyDocumentLanguage = (lang: string): void => {
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
};

// The i18next browser detector used to default to the 'i18nextLng' storage key,
// while LanguageContext read/wrote 'language' — two keys for one preference.
// Everything now shares 'language'; drop any stale value from the old key.
try {
  localStorage.removeItem('i18nextLng');
} catch {
  // Storage unavailable (private mode / SSR) — detection simply falls back to navigator.
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: ['en', 'fr', 'ar'],
    debug: false,
    // SINGLE TRANSLATION SOURCE (system 2 of the former triple setup):
    // `menuTranslations` is bundled into the binary, so init runs fully
    // synchronously (i18next skips its async path when `resources` is present)
    // and no `/locales/<lng>/translation.json` request — i.e. no 404 for
    // en/ar — can ever fire. i18next-http-backend is therefore not used.
    resources: {
      fr: { translation: menuTranslations.fr },
      ar: { translation: menuTranslations.ar },
      en: { translation: menuTranslations.en },
    },
    interpolation: {
      escapeValue: false,
    },
    detection: {
      // Same storage key LanguageContext reads/writes: 'language'.
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'language',
    },
    react: {
      useSuspense: false,
    },
  });

// Synchronous initialization means i18n.resolvedLanguage is final here:
// stored 'language' (if valid) -> navigator (region subtags normalized to
// supportedLngs, e.g. fr-MA -> fr) -> fallbackLng 'en'. Reflect it in the
// document before React renders anything.
applyDocumentLanguage(i18n.resolvedLanguage || i18n.language || 'en');

// Note: no `changeLanguage` monkey-patch — the only caller (LanguageContext)
// awaits it and handles/logs failures itself.

export default i18n;
